import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { contact, services } from "@/data/content";

const FOOTER_NAV = [
    { to: "/", label: "Home", id: "home" },
    { to: "/about", label: "About", id: "about" },
    { to: "/expertise", label: "Expertise", id: "expertise" },
    { to: "/experience", label: "Experience", id: "experience" },
    { to: "/approach", label: "Approach", id: "approach" },
    { to: "/contact", label: "Contact", id: "contact" },
];

export default function Footer() {
    return (
        <footer className="border-t-2 border-[#243650] bg-[#0B1322]" data-testid="footer">
            <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
                <div className="grid gap-12 lg:grid-cols-[1.3fr_0.6fr_1fr_1.1fr]">
                    <div>
                        <div className="flex items-center gap-4">
                            <span className="inline-flex items-center bg-[#F2EEE4] px-2.5 py-2 shadow-[4px_4px_0_rgba(217,168,78,0.5)]">
                                <img src="/logo.png" alt="Infrasic Intelligence emblem" className="h-14 w-auto" />
                            </span>
                            <span className="leading-none">
                                <span className="block font-display text-2xl font-bold tracking-[0.06em] text-[#E8ECF4]">
                                    INFRASIC
                                </span>
                                <span className="mt-1.5 block font-tech text-[9px] tracking-[0.42em] text-[#5B93E8]">
                                    INTELLIGENCE
                                </span>
                            </span>
                        </div>
                        <p className="mt-6 font-tech text-[10px] uppercase tracking-[0.22em] text-[#8C97AC]">
                            Forensic Investigations | Financial Intelligence | Digital Forensics
                        </p>
                        <p className="mt-4 max-w-sm font-display text-lg font-medium leading-relaxed text-[#E8ECF4]">
                            Forensic Intelligence. Financial Insight. Technical Expertise.
                        </p>
                    </div>

                    <div>
                        <h4 className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                            Navigation
                        </h4>
                        <ul className="mt-6 space-y-3 text-sm">
                            {FOOTER_NAV.map((link) => (
                                <li key={link.id}>
                                    <Link
                                        to={link.to}
                                        data-testid={`footer-link-${link.id}`}
                                        className="text-[#9AA7BF] transition-colors duration-300 hover:text-[#5B93E8]"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                            Expertise
                        </h4>
                        <ul className="mt-6 space-y-3 text-sm">
                            {services.map((s) => (
                                <li key={s.id}>
                                    <Link
                                        to={`/expertise#${s.id}`}
                                        data-testid={`footer-service-${s.id}`}
                                        className="text-[#9AA7BF] transition-colors duration-300 hover:text-[#5B93E8]"
                                    >
                                        {s.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                            Contact
                        </h4>
                        <ul className="mt-6 space-y-4 text-sm text-[#9AA7BF]">
                            <li className="flex items-start gap-3">
                                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#5B93E8]" />
                                <a href={contact.phoneHref} data-testid="footer-phone" className="transition-colors duration-300 hover:text-[#5B93E8]">
                                    {contact.phone}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#5B93E8]" />
                                <a href={`mailto:${contact.email}`} data-testid="footer-email" className="break-all transition-colors duration-300 hover:text-[#5B93E8]">
                                    {contact.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#5B93E8]" />
                                <span data-testid="footer-address">{contact.address}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Linkedin className="mt-0.5 h-4 w-4 shrink-0 text-[#5B93E8]" />
                                <a
                                    href={contact.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-linkedin"
                                    className="transition-colors duration-300 hover:text-[#5B93E8]"
                                >
                                    Connect on LinkedIn
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="border-t border-[#243650]">
                <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-8 lg:flex-row lg:items-center lg:justify-between lg:px-10">
                    <p className="font-tech text-[11px] tracking-wide text-[#8C97AC]" data-testid="footer-copyright">
                        &copy; Infrasic Intelligence (OPC) Private Limited. All Rights Reserved.
                    </p>
                    <p className="font-tech text-[11px] tracking-wide text-[#8C97AC]" data-testid="footer-registrations">
                        DPIIT Startup Recognition: <span className="text-[#5B93E8]">{contact.dpiit}</span>
                        <span className="mx-2 text-[#243650]">|</span>
                        Udyam: <span className="text-[#5B93E8]">{contact.udyam}</span>
                    </p>
                    <div className="flex items-center gap-4 font-tech text-[11px] uppercase tracking-[0.16em] text-[#8C97AC]">
                        <Link to="/privacy-policy" data-testid="footer-privacy-policy" className="text-[#9AA7BF] transition-colors duration-300 hover:text-[#5B93E8]">
                            Privacy Policy
                        </Link>
                        <span className="text-[#243650]">|</span>
                        <Link to="/terms" data-testid="footer-terms" className="text-[#9AA7BF] transition-colors duration-300 hover:text-[#5B93E8]">
                            Terms
                        </Link>
                        <span className="text-[#243650]">|</span>
                        <Link to="/contact" data-testid="footer-legal-contact" className="text-[#9AA7BF] transition-colors duration-300 hover:text-[#5B93E8]">
                            Contact
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
